"use client"

import { FileText } from "lucide-react"
import { cn } from "@/lib/utils"
import type { CardFact } from "./extracted-evidence"

interface CitationCardProps {
  fact: CardFact
  /**
   * Fired when the user clicks the "Source: page N" footer. Receives the
   * 1-based page number. Omit (or omit a citation page) to render the
   * source as non-clickable text — still honest.
   */
  onJumpToPage?: (page: number) => void
}

/**
 * Renders a single extracted fact as an evidence card:
 *   - label header + value
 *   - the verbatim citation snippet in a quoted block (only when present)
 *   - a "Source: page N" footer button (only when a page is present and a
 *     jump handler is wired)
 *
 * HONEST DEGRADATION: a fact with no citation renders VALUE-ONLY — no
 * quote block, no footer. We never show a blank quote or a broken link.
 */
export function CitationCard({ fact, onJumpToPage }: CitationCardProps) {
  const snippet = fact.citation?.snippet?.trim()
  const page = fact.citation?.page
  const canJump = typeof page === "number" && page > 0 && !!onJumpToPage

  return (
    <div className="rounded-2xl border border-white/10 bg-neutral-800 p-4 text-white shadow-md">
      {fact.label && (
        <p className="text-xs uppercase tracking-wide text-white/50">
          {fact.label}
        </p>
      )}
      {fact.value && (
        <p className="mt-1 text-base font-medium leading-snug text-white">
          {fact.value}
        </p>
      )}

      {snippet && (
        <blockquote className="mt-3 border-l-2 border-white/20 pl-3 text-sm italic leading-relaxed text-white/70">
          &ldquo;{snippet}&rdquo;
        </blockquote>
      )}

      {typeof page === "number" && page > 0 && (
        <div className="mt-3 flex items-center">
          {canJump ? (
            <button
              type="button"
              onClick={() => onJumpToPage?.(page)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1",
                "text-xs text-white/70 transition-colors hover:bg-white/10 hover:text-white",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
              )}
              aria-label={`Jump to page ${page} in the contract`}
              title={`Jump to page ${page}`}
            >
              <FileText className="size-3.5" />
              Source: page {page}
            </button>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs text-white/50">
              <FileText className="size-3.5" />
              Source: page {page}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
